'use client'

import Image from 'next/image'
import NextLink from 'next/link'
import { useTranslations } from 'next-intl'
import { type ReactNode } from 'react'

import { nextLinkHrefFromPathWithOptionalHash } from '@/_lib/next-link-href'
import {
	getCheapestSkMainInsurancePrice,
	getSelfPaidInsurancePrice
} from '@/_lib/order/template-visit-pricing'
import {
	cn,
	formatPrice,
	getFullName,
	getLocaleName,
	getMinimumPatientAge,
	orderFlowPriceFormatOptions
} from '@/_lib/utils'
import { ITemplate, ITemplateTimeslot } from '@/_types'
import { AMBULANCE_PRICE_LIST_TABLE_TYPE, IAmbulance } from '@/_types/ambulance'
import { SECTION_THEME_ENUM } from '@/_types/base'

import { useOrderFlow } from '../../../../../_context/order-flow-context'
import { AOrderDoctorBlock } from '../../../../a-order-doctor-block'
import { ZOrderProductCard } from '../z-order-product-card'

import {
	AProductTemplateCalendarExpandedPanel,
	AProductTemplateCalendarQuickRow,
	useProductTemplateCalendar
} from './template-calendar/a-product-template-calendar'

const insuranceCompaniesLogosMap: Record<
	string,
	{
		name: string
		logo: string
		alt: string
		className?: string
		height: number
		width: number
		sortOrder: number
	}
> = {
	'2700': {
		name: 'Union',
		logo: `${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/insurance-companies/union.svg`,
		alt: 'Union',
		className: 'union',
		height: 25,
		width: 80,
		sortOrder: 1
	},
	'2500': {
		name: 'VSZP',
		logo: `${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/insurance-companies/vszp-short.png`,
		alt: 'VSZP',
		className: 'vszp mt-[-6px]',
		height: 27,
		width: 45,
		sortOrder: 2
	},
	'2400': {
		name: 'Dôvera',
		logo: `${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/insurance-companies/dovera.png`,
		alt: 'Dôvera',
		className: 'dovera -mt-2',
		height: 19,
		width: 77,
		sortOrder: 3
	}
}

function AOrderTemplateInsuranceCompanies({
	ambulance,
	className
}: {
	ambulance?: IAmbulance
	className?: string
}) {
	const t = useTranslations('pages.order')

	if (!ambulance?.insuranceCompanies?.length) {
		return null
	}

	const sortedInsuranceCompanies = [...ambulance.insuranceCompanies].sort((a, b) => {
		const orderA = insuranceCompaniesLogosMap[a.code]?.sortOrder ?? 999
		const orderB = insuranceCompaniesLogosMap[b.code]?.sortOrder ?? 999
		return orderA - orderB
	})

	return (
		<div
			className={cn(
				'flex flex-col gap-2 lg:gap-3 border-t border-b lg:border-b-0 border-medante-purple-dark/15 py-3 lg:pb-0',
				className
			)}
		>
			<p className='text-sm text-medante-purple-dark font-medium'>
				{t('ambulanceInsuredBy')}
			</p>
			<ul className='flex flex-wrap items-center gap-6'>
				{sortedInsuranceCompanies.map(insurance => {
					const meta = insuranceCompaniesLogosMap[insurance.code]

					if (!meta) {
						return (
							<li key={insurance.id}>
								<span className='text-sm font-medium text-medante-purple-dark'>
									{insurance.name}
								</span>
							</li>
						)
					}

					return (
						<li key={insurance.id} className='flex items-center'>
							<Image
								src={meta.logo}
								alt={meta.alt}
								width={meta.width}
								height={meta.height}
								className={cn(meta.className)}
								unoptimized={true}
							/>
						</li>
					)
				})}
			</ul>
		</div>
	)
}

// ---------------------------------------------------------------------------
// Price breakdown block (step 2)
// ---------------------------------------------------------------------------

/**
 * Renders the price rows on a template product card (step 2).
 *
 * Display rules by ambulance type:
 *
 * PLUS_DEFAULT_PRICE_FOR_INSURED
 *   All insurers pay the same fixed amount — show only the self-payer row,
 *   without an "od" prefix, so the patient sees the exact price upfront.
 *
 * DEFAULT | PLUS_WITH_AND_WITHOUT_YEAR_PROGRAM_PRICE
 *   Show three rows:
 *   - "Manažment návštevy" — cheapest of the 3 SK public insurers, prefixed "od".
 *   - "Poistenci" — always 0 € (covered by public insurance).
 *   - "Samoplatca" — SA00 price, prefixed "od".
 */
const AOrderTemplatePrice = ({
	template,
	ambulance,
	className
}: {
	template: ITemplate
	ambulance?: IAmbulance
	className?: string
}) => {
	const { ambulancePriceListTableType } = useOrderFlow()

	const ambulanceLink = nextLinkHrefFromPathWithOptionalHash(
		template?.workplace?.ambulance?.slug
			? `/ambulancie/${template.workplace.ambulance.slug}/#cennik`
			: '/ambulancie'
	)

	const formatEuro = (amount: number | null) => {
		const f = formatPrice(amount ?? null, orderFlowPriceFormatOptions)
		return `${f.price} ${f.currency}`
	}

	const APriceItem = ({
		label,
		description,
		price
	}: {
		label: string
		description: ReactNode
		price: string
	}) => (
		<div className='flex gap-4 items-start'>
			<div className='min-w-0 flex-1'>
				<p className='text-sm font-medium text-medante-purple-dark md:text-base'>{label}</p>
				<div className='text-xs font-light text-medante-purple-dark/85 md:text-sm'>
					{description}
				</div>
			</div>
			{price && (
				<p className='w-fit shrink-0 text-right text-base font-normal text-medante-purple-dark md:text-lg md:font-medium'>
					{price}
				</p>
			)}
		</div>
	)

	// ------------------------------------------------------------------
	// PLUS_DEFAULT_PRICE_FOR_INSURED — fixed price, self-payer row only
	// ------------------------------------------------------------------
	if (
		ambulancePriceListTableType ===
		AMBULANCE_PRICE_LIST_TABLE_TYPE.PLUS_DEFAULT_PRICE_FOR_INSURED
	) {
		const fixedPrice =
			getSelfPaidInsurancePrice(template.insuranceCompanies, template.price) ??
			template.price ??
			0

		return (
			<div className={cn('flex flex-col gap-2', className)}>
				<APriceItem
					label='Samoplatca'
					description={
						<NextLink
							href={ambulanceLink}
							prefetch={false}
							className='decoration-medante-purple-dark/40 underline-offset-2 hover:opacity-80 transition-opacity duration-200'
						>
							Podľa aktuálneho <span className='underline'>cenníka ambulancie</span>
						</NextLink>
					}
					price={formatEuro(fixedPrice)}
				/>

				<AOrderTemplateInsuranceCompanies
					ambulance={ambulance}
					className='hidden lg:flex'
				/>
			</div>
		)
	}

	// ------------------------------------------------------------------
	// DEFAULT / PLUS_WITH — three rows with "od" prefix
	// ------------------------------------------------------------------
	const managementAmount =
		getCheapestSkMainInsurancePrice(template.insuranceCompanies, template.price) ??
		template.price ??
		0
	const selfPaidAmount =
		getSelfPaidInsurancePrice(template.insuranceCompanies, template.price) ??
		template.price ??
		0

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			<APriceItem
				label='Manažment návštevy'
				description='Služby, ktoré nie sú hradené z verejného zdravotného poistenia'
				price={`od ${formatEuro(managementAmount)}`}
			/>
			{/* <APriceItem label='Poistenci' description='Poistenci s výmenným lístkom' price='0 €' /> */}
			<APriceItem
				label='Samoplatca'
				description={
					<NextLink
						href={ambulanceLink}
						prefetch={false}
						className=' decoration-medante-purple-dark/40 underline-offset-2 hover:opacity-80 transition-opacity duration-200'
					>
						Podľa aktuálneho <span className='underline'>cenníka ambulancie</span>
					</NextLink>
				}
				price={`od ${formatEuro(selfPaidAmount)}`}
			/>

			<AOrderTemplateInsuranceCompanies ambulance={ambulance} className='hidden lg:flex' />
		</div>
	)
}

/**
 * Doctor block: photo, name, position, age range, and optional languages.
 *
 * @param props - Doctor section props.
 * @param props.template - Template providing `employee` or `workplace.employee`.
 * @param props.className - Optional layout classes on the outer container.
 * @returns `null` when no employee can be resolved from the template.
 */
const AOrderTemplateDoctor = ({
	template,
	className
}: {
	template: ITemplate
	className?: string
}) => {
	const t_ageRange = useTranslations('components.ageRange')
	const t_common = useTranslations('common')
	const t_summary = useTranslations('pages.order.summary')

	const employee = template.employee ?? template.workplace?.employee
	if (!employee) return null

	const ageLabel = t_ageRange('services', {
		ageRange:
			getMinimumPatientAge(
				employee.minimumPatientAge ?? null,
				employee.maximumPatientAge ?? null,
				t_ageRange
			) || ''
	})

	const languagesLabel =
		employee.languages && employee.languages.length > 0
			? `${t_summary('language')} ${employee.languages.map(lang => getLocaleName(lang, t_common)).join(', ')}`
			: null

	return (
		<AOrderDoctorBlock
			className={className}
			photoUrl={employee.photo?.url ?? null}
			fullName={getFullName(employee)}
			position={employee.position ?? null}
			ageLabel={ageLabel}
			languagesLabel={languagesLabel}
		/>
	)
}

/**
 * Template card when the API returned no `timeslots` — still shows name, pricing, and doctor.
 *
 * @param template - Template without bookable slots.
 */
function AOrderTemplateCardEmptyView({
	template,
	ambulance,
	sectionTheme
}: {
	template: ITemplate
	ambulance?: IAmbulance
	sectionTheme: SECTION_THEME_ENUM | null
}) {
	return (
		<ZOrderProductCard sectionTheme={sectionTheme}>
			<div className='grid grid-cols-1 gap-y-4 lg:grid-cols-2 lg:gap-x-12 lg:gap-y-4'>
				<div className='min-w-0'>
					<p className='mb-2 text-lg font-semibold lg:font-medium text-medante-purple-dark md:text-xl'>
						{template.name}
					</p>
					<AOrderTemplatePrice
						template={template}
						ambulance={ambulance}
						className='mb-2'
					/>
				</div>
				<div className='flex flex-col-reverse lg:flex-col gap-3 lg:gap-4 border-medante-purple-dark/15 lg:border-l lg:pl-12 lg:pt-4'>
					<AOrderTemplateDoctor template={template} />
					<AOrderTemplateInsuranceCompanies ambulance={ambulance} className='lg:hidden' />
					<p className='text-xs font-light text-medante-purple-dark/80'>
						Nenašli sme žiadny dostupný termín.
					</p>
				</div>
			</div>
		</ZOrderProductCard>
	)
}

/**
 * Template card with quick slot row and expandable week calendar when slots exist.
 *
 * @param props - View props.
 * @param props.template - Template including `timeslots` used by {@link useProductTemplateCalendar}.
 * @param props.onSelect - Optional handler receiving the chosen template and `ITemplateTimeslot`.
 */
function AOrderTemplateCardWithSlotsView({
	template,
	ambulance,
	onSelect,
	sectionTheme
}: {
	template: ITemplate
	ambulance?: IAmbulance
	onSelect?: (template: ITemplate, timeslot: ITemplateTimeslot) => void
	sectionTheme: SECTION_THEME_ENUM | null
}) {
	const cal = useProductTemplateCalendar(template, onSelect)

	return (
		<ZOrderProductCard sectionTheme={sectionTheme}>
			<div className='grid grid-cols-1 gap-y-4 lg:grid-cols-2 lg:gap-x-12 lg:gap-y-4'>
				<div className='min-w-0'>
					<p className='text-base/5 font-semibold lg:font-medium text-medante-purple-dark md:text-xl'>
						{template.name}
					</p>
					{template.description && template.description.length > 0 && (
						<p className='mb-2 text-sm font-light text-medante-purple-dark/80'>
							{template.description}
						</p>
					)}
					<AOrderTemplatePrice
						template={template}
						ambulance={ambulance}
						className='mb-2 mt-1'
					/>
				</div>
				<div className='flex flex-col-reverse lg:flex-col gap-3 lg:gap-4 border-medante-purple-dark/15 lg:border-l lg:pl-12 lg:pt-4'>
					<AOrderTemplateDoctor template={template} className='mt-2 lg:mt-0' />

					<AOrderTemplateInsuranceCompanies ambulance={ambulance} className='lg:hidden' />

					<div
						className={cn(
							template.employee &&
								'lg:border-t lg:border-medante-purple-dark/15 lg:pt-4'
						)}
					>
						<AProductTemplateCalendarQuickRow
							template={cal.template}
							calendarOpen={cal.calendarOpen}
							toggleCalendar={cal.toggleCalendar}
							emitTimeslot={cal.emitTimeslot}
						/>
					</div>
				</div>
				{cal.calendarOpen && cal.calendarRange ? (
					<div className='w-full min-w-0 border-t border-medante-purple-dark/15 pt-4 lg:col-span-2'>
						<AProductTemplateCalendarExpandedPanel
							templateId={cal.template.id}
							calendarRange={cal.calendarRange}
							calendarSlots={cal.calendarSlots}
							isSlotsLoading={cal.isCalendarSlotsPending}
							onSlotSelect={cal.handleCalendarSlotSelect}
							onWeekChange={cal.handleWeekChange}
							sectionTheme={sectionTheme}
						/>
					</div>
				) : null}
			</div>
		</ZOrderProductCard>
	)
}

/**
 * Chooses empty-state vs calendar view based on `template.timeslots.length`.
 *
 * @param props - Same as {@link AOrderTemplateCardWithSlotsView}.
 */
function AOrderTemplateCardView({
	template,
	ambulance,
	onSelect,
	sectionTheme
}: {
	template: ITemplate
	ambulance?: IAmbulance
	onSelect?: (template: ITemplate, timeslot: ITemplateTimeslot) => void
	sectionTheme: SECTION_THEME_ENUM | null
}) {
	if (template.timeslots.length === 0) {
		return (
			<AOrderTemplateCardEmptyView
				sectionTheme={sectionTheme}
				template={template}
				ambulance={ambulance}
			/>
		)
	}
	return (
		<AOrderTemplateCardWithSlotsView
			sectionTheme={sectionTheme}
			template={template}
			ambulance={ambulance}
			onSelect={onSelect}
		/>
	)
}

/**
 * Public template product card: pricing, doctor, quick slots, and optional expanded calendar.
 *
 * @param props - Card props.
 * @param props.template - Workplace booking template.
 * @param props.ambulance - Ambulance.
 * @param props.onSelect - Called when the user picks a quick slot or a calendar slot.
 */
export const AOrderTemplateCard = (props: {
	template: ITemplate
	ambulance?: IAmbulance
	onSelect?: (template: ITemplate, timeslot: ITemplateTimeslot) => void
	sectionTheme?: SECTION_THEME_ENUM | null
}) => {
	return (
		<AOrderTemplateCardView
			sectionTheme={props.sectionTheme ?? null}
			template={props.template}
			ambulance={props.ambulance}
			onSelect={props.onSelect}
		/>
	)
}
